/**
 * Turn short LaTeX fragments (exercise titles, sheet titles) into readable
 * plain text for the UI and for Typst, which has no idea about `\"a`.
 *
 * This is deliberately limited to the constructs that actually occur in
 * titles — accents, escaped specials, simple font switches and light maths.
 */

const ACCENTS: Record<string, Record<string, string>> = {
  '"': { a: 'ä', o: 'ö', u: 'ü', A: 'Ä', O: 'Ö', U: 'Ü', e: 'ë', i: 'ï', y: 'ÿ' },
  "'": { a: 'á', e: 'é', i: 'í', o: 'ó', u: 'ú', A: 'Á', E: 'É', I: 'Í', O: 'Ó', U: 'Ú', n: 'ń' },
  '`': { a: 'à', e: 'è', i: 'ì', o: 'ò', u: 'ù', A: 'À', E: 'È', O: 'Ò', U: 'Ù' },
  '^': { a: 'â', e: 'ê', i: 'î', o: 'ô', u: 'û', A: 'Â', E: 'Ê', O: 'Ô', U: 'Û' },
  '~': { a: 'ã', n: 'ñ', o: 'õ', A: 'Ã', N: 'Ñ', O: 'Õ' },
  c: { c: 'ç', C: 'Ç' },
  v: { s: 'š', c: 'č', z: 'ž', r: 'ř', e: 'ě' },
};

const SYMBOLS: Record<string, string> = {
  ss: 'ß',
  ae: 'æ',
  AE: 'Æ',
  oe: 'œ',
  OE: 'Œ',
  o: 'ø',
  O: 'Ø',
  aa: 'å',
  AA: 'Å',
  ldots: '…',
  dots: '…',
  times: '×',
  cdot: '·',
  to: '→',
  rightarrow: '→',
  leftarrow: '←',
  Rightarrow: '⇒',
  leq: '≤',
  geq: '≥',
  neq: '≠',
  in: '∈',
  forall: '∀',
  exists: '∃',
  alpha: 'α',
  beta: 'β',
  gamma: 'γ',
  delta: 'δ',
  lambda: 'λ',
  mu: 'μ',
  pi: 'π',
  sigma: 'σ',
  omega: 'ω',
  Sigma: 'Σ',
  Omega: 'Ω',
  NN: 'ℕ',
  ZZ: 'ℤ',
  RR: 'ℝ',
  BB: '𝔹',
  cons: '::',
  vdash: '⊢',
  vartriangleright: '▷',
  lightning: '↯',
  Lightning: '↯',
  bigo: 'O',
  infty: '∞',
};

/** Drop the wrapper but keep the argument. */
const TRANSPARENT = new Set([
  'text',
  'textbf',
  'textit',
  'textrm',
  'texttt',
  'textsc',
  'emph',
  'mathit',
  'mathbf',
  'mathrm',
  'mathtt',
  'mbox',
  'mi',
  'lstinline',
  'operatorname',
  'ensuremath',
]);

export function unescapeTex(input: string): string {
  if (!input) return '';
  let s = input;

  // {\"a} and \"{a} and \"a
  s = s.replace(/\{?\\([\"'`^~cv])\{?([A-Za-z])\}?\}?/g, (full, acc: string, letter: string) => {
    return ACCENTS[acc]?.[letter] ?? full;
  });

  // \ss, \alpha, … (longest names first so \Sigma wins over \sigma variants)
  const names = Object.keys(SYMBOLS).sort((a, b) => b.length - a.length);
  s = s.replace(new RegExp(`\\\\(${names.join('|')})(?![A-Za-z])\\s?`, 'g'), (_m, name: string) => SYMBOLS[name]);

  // \textbf{…} → …  (repeat for nesting)
  for (let i = 0; i < 4; i++) {
    s = s.replace(/\\([A-Za-z]+)\s*\{([^{}]*)\}/g, (full, cmd: string, arg: string) =>
      TRANSPARENT.has(cmd) ? arg : full,
    );
  }

  // superscripts used in dates: 21$^\text{st}$
  s = s.replace(/\^\{?([A-Za-z0-9]+)\}?/g, '$1');

  // escaped specials. `\$` is a dollar sign, not a maths delimiter, so it is held aside
  // until the delimiters are stripped below — "costs 5\$" used to come out as "costs 5"
  s = s.replace(/\\\$/g, '\uE001');
  s = s.replace(/\\([&%#_{}])/g, '$1');
  // dashes and ties
  s = s.replace(/---/g, '—').replace(/--/g, '–').replace(/~/g, ' ');
  // leftover math delimiters and grouping
  s = s.replace(/\$+/g, '').replace(/[{}]/g, '').replace(/\uE001/g, '$');
  // any remaining control sequence
  s = s.replace(/\\[A-Za-z]+\s?/g, '').replace(/\\(.)/g, '$1');

  return s.replace(/\s{2,}/g, ' ').trim();
}

/** Display helper: title, falling back to the printed number. */
export function displayTitle(
  title: string | null | undefined,
  fallback: string | null | undefined,
): string {
  const t = unescapeTex(title ?? '');
  return t || fallback || '';
}

/*
 * The other direction: text going *into* LaTeX.
 *
 * Nothing in the codebase did this, and every builder spliced titles and headings into
 * the document as they were typed. That is right for a corpus title, which is LaTeX
 * already, and wrong for anything typed as plain text — a Typst exercise's title, a
 * heading on a Typst sheet. `Lists & Trees` stopped pdflatex with "Misplaced alignment
 * tab character &", `50% bonus` commented out the `]` that closed the title argument,
 * and `snake_case` gave "Missing $ inserted". Tutors who do not write LaTeX hit this
 * the first time they typed a title.
 */

const TEX_SPECIALS: Record<string, string> = {
  '\\': '\\textbackslash{}',
  '{': '\\{',
  '}': '\\}',
  $: '\\$',
  '&': '\\&',
  '#': '\\#',
  '%': '\\%',
  _: '\\_',
  '^': '\\^{}',
  '~': '\\~{}',
  '<': '\\textless{}',
  '>': '\\textgreater{}',
  '|': '\\textbar{}',
};

/**
 * Plain text as LaTeX that prints exactly those characters.
 *
 * For values known to be plain — they were typed for Typst, or into a field that is
 * not LaTeX. Every character LaTeX treats specially is escaped, the backslash included,
 * so the result never runs a command. `<`, `>` and `|` are spelled out because the
 * default OT1 encoding prints `¡`, `¿` and `—` for them.
 */
export function escapeTex(text: string): string {
  return text.replace(/[\\{}$&#%_^~<>|]/g, (c) => TEX_SPECIALS[c]);
}

/**
 * A short field that is *meant* as LaTeX but may well be plain text, made safe to set.
 *
 * Headings and sheet titles are LaTeX by contract — `\"u`, `$\lambda$` and `\emph{…}`
 * appear in them and must survive — yet they are typed by people who may not know it.
 * So only what cannot be meant as markup in a one-line title is escaped:
 *
 *  - an unescaped `&`, `%` or `#` — an alignment tab, a comment that swallows the
 *    closing brace, a macro parameter: each is an error or a silent truncation here;
 *  - `_` and `^` outside maths, where they are "Missing $ inserted";
 *  - every `$`, when there is an odd number of them: an unbalanced dollar is a price,
 *    not maths, and would put the rest of the document in maths mode.
 *
 * Anything already escaped (`\&`, `\_`) is left alone, as is every command.
 */
/** `{…}` starting at `at` (after optional spaces), braces balanced, or '' if none. */
function braceGroup(text: string, at: number): string {
  let i = at;
  while (text[i] === ' ') i++;
  if (text[i] !== '{') return '';
  let depth = 0;
  for (let j = i; j < text.length; j++) {
    if (text[j] === '\\') {
      j++;
      continue;
    }
    if (text[j] === '{') depth++;
    else if (text[j] === '}' && --depth === 0) return text.slice(at, j + 1);
  }
  return '';
}

export function texSafe(text: string): string {
  const unescapedDollars = (text.match(/(?<!\\)(?:\\\\)*\$/g) ?? []).length;
  const literalDollars = unescapedDollars % 2 === 1;
  let out = '';
  let math = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '\\') {
      // a command or an escaped character: copy it whole, so `\&` stays `\&`
      const word = /^\\(?:[A-Za-z]+|.)?/s.exec(text.slice(i))![0];
      out += word;
      i += word.length - 1;
      // `\(…\)` and `\[…\]` are maths as much as `$…$` is: a subscript inside stays one
      if (word === '\\(' || word === '\\[') math = true;
      else if (word === '\\)' || word === '\\]') math = false;
      // arguments that are not text: a URL, or maths — copied untouched, so `a_b#c` in a
      // link is not turned into `a\_b\#c`
      else if (word === '\\href' || word === '\\url' || word === '\\ensuremath') {
        const group = braceGroup(text, i + 1);
        if (group) {
          out += group;
          i += group.length;
        }
      }
      continue;
    }
    if (c === '$') {
      if (literalDollars) out += '\\$';
      else {
        math = !math;
        out += c;
      }
      continue;
    }
    if (c === '&' || c === '%' || c === '#') {
      out += `\\${c}`;
      continue;
    }
    if (!math && c === '_') {
      out += '\\_';
      continue;
    }
    if (!math && c === '^') {
      out += '\\^{}';
      continue;
    }
    out += c;
  }
  return out;
}
